import api from '../../../lib/axios';
import { BaseResponse } from '../../../lib/api-contract';
import { LocationData } from './MerchantHeatmap.data';
import {
    LiveEventsMapFilter,
    LiveEventsMapStats,
    LiveEventsMapStatsRawApiResponse,
    LiveEventsMapMapRawApiResponse,
    LiveEventsMapActivityLevelsRawApiResponse,
    LiveEventsMapEventPerformanceRawApiResponse,
    LiveEventAreaItem,
    EventPerformanceRow,
    LiveEventOption,
    LiveEventLocationOption,
} from './MerchantLiveEventsMap.data';

const BASE = '/v1/merchant/live-events-map';

/** Build request body for POST endpoints that accept filter */
const buildFilterBody = (filter: LiveEventsMapFilter) => ({
    filter: {
        event_ids: filter.event_ids ?? [],
        location_ids: filter.location_ids ?? [],
        demographic_age: filter.demographic_age ?? '',
        quick_filter_days: filter.quick_filter_days,
        start_date: filter.start_date,
        end_date: filter.end_date,
    },
});

// --- 1. Statistic: GET event-stats (data: { total_events: {value, completed, upcoming}, total_attendees: {value, description}, average_show_rate: {value, description}, event_to_redemption_rate: {value, description, attendees_redeemed, total_attendees} }) ---
export const getLiveEventsMapStats = async (): Promise<LiveEventsMapStats> => {
    const response = await api.get<BaseResponse<LiveEventsMapStatsRawApiResponse>>(`${BASE}/event-stats`);
    const raw = response.data?.data;
    const te = raw?.total_events;
    const ta = raw?.total_attendees;
    const asr = raw?.average_show_rate;
    const etr = raw?.event_to_redemption_rate;
    const totalEvents = te?.value ?? 0;
    const completed = te?.completed ?? 0;
    const upcoming = te?.upcoming ?? 0;
    const totalAttendees = ta?.value ?? 0;
    const avgShow = asr?.value ?? 0;
    const eventToRedemption = etr?.value ?? 0;
    const redeemed = etr?.attendees_redeemed ?? 0;
    return {
        totalEvents,
        totalEventsSubtext: `${completed} completed, ${upcoming} upcoming`,
        totalAttendees,
        totalAttendeesSubtext: ta?.description ?? 'Verified check-ins across all events',
        avgShowRatePercent: avgShow,
        avgShowRateSubtext: asr?.description ?? (totalEvents > 0 ? `${totalAttendees} attendees ÷ ${totalEvents} events` : '—'),
        eventToRedemptionPercent: eventToRedemption,
        eventToRedemptionSubtext: etr?.description ?? (redeemed > 0 ? `${redeemed} of ${totalAttendees} attendees redeemed coupons` : '—'),
    };
};

// --- 2. Map: POST map (data: { markers: [{ location_id, location_name, latitude, longitude, attendee_count, density_level }], total_attendees, areas_covered }) ---
export const getLiveEventsMapMap = async (filter: LiveEventsMapFilter): Promise<LocationData[]> => {
    const response = await api.post<BaseResponse<LiveEventsMapMapRawApiResponse>>(
        `${BASE}/map`,
        buildFilterBody(filter),
        { headers: { 'Content-Type': 'application/json' } }
    );
    const payload = response.data?.data;
    const markers = payload?.markers ?? [];
    return markers.map((m) => {
        let redemption_rate = 50;
        if (m.density_level === 'high') redemption_rate = 70;
        else if (m.density_level === 'medium') redemption_rate = 50;
        else if (m.density_level === 'low') redemption_rate = 30;
        return {
            id: m.location_id,
            name: m.location_name,
            latitude: m.latitude,
            longitude: m.longitude,
            redemptions: m.attendee_count,
            percentage_of_total: 0,
            top_coupon: 'N/A',
            redemption_rate,
        };
    });
};

// --- 3. Activity Levels: POST activity-levels (data.locations[].attendee_count, demographics_age_range) ---
export const getLiveEventsMapActivityLevels = async (
    filter: LiveEventsMapFilter
): Promise<LiveEventAreaItem[]> => {
    const response = await api.post<BaseResponse<LiveEventsMapActivityLevelsRawApiResponse>>(
        `${BASE}/activity-levels`,
        buildFilterBody(filter),
        { headers: { 'Content-Type': 'application/json' } }
    );
    const payload = response.data?.data;
    const locations = payload?.locations ?? [];
    const total = locations.reduce((sum, loc) => sum + (loc.attendee_count ?? 0), 0);
    return locations.map((loc) => {
        const attendees = loc.attendee_count ?? 0;
        const pct = total > 0 ? (attendees / total) * 100 : (loc.percentage_of_total ?? 0);
        return {
            id: loc.location_id ?? '',
            name: loc.location_name ?? 'Unknown',
            attendees,
            percentage_of_total: pct,
            most_popular_event: loc.most_popular_event ?? '—',
            demographics: loc.demographics_age_range ?? '—',
        };
    });
};

// --- 4. Event Performance Table: POST event-performance-table (data.events[].event_id, event_name, date "04/20/2026", rsvp, attended, show_rate) ---
export const getLiveEventsMapEventPerformanceTable = async (
    filter: LiveEventsMapFilter
): Promise<EventPerformanceRow[]> => {
    const response = await api.post<BaseResponse<LiveEventsMapEventPerformanceRawApiResponse>>(
        `${BASE}/event-performance-table`,
        buildFilterBody(filter),
        { headers: { 'Content-Type': 'application/json' } }
    );
    const payload = response.data?.data;
    const rows = payload?.events ?? [];
    return rows.map((row) => ({
        id: row.event_id ?? '',
        eventName: row.event_name ?? '—',
        date: row.date ?? '—',
        rsvp: row.rsvp ?? 0,
        attended: row.attended ?? 0,
        showRatePercent: row.show_rate ?? 0,
    }));
};

// --- 5. Event options (dropdown): use merchant event list ---
export const getLiveEventOptions = async (): Promise<LiveEventOption[]> => {
    try {
        const { fetchMerchantEventList } = await import('../merchant-event/MerchantEvent.api');
        const res = await fetchMerchantEventList({
            pagination: { index: 0, size: 500 },
            filter: {},
        });
        return (res.list ?? []).map((e: { id: string; title?: string; name?: string }) => ({
            id: e.id,
            name: e.title ?? e.name ?? e.id,
            title: e.title ?? e.name,
        }));
    } catch {
        return [];
    }
};

// --- 6. Location options (dropdown): GET neighborhoods ---
export const getLiveEventLocationOptions = async (): Promise<LiveEventLocationOption[]> => {
    const response = await api.get<BaseResponse<LiveEventLocationOption[]>>(
        '/v1/merchant/organization-population/neighborhoods'
    );
    const data = response.data?.data;
    if (Array.isArray(data)) {
        return data.map((item: { id: string; name: string }) => ({
            id: item.id,
            name: item.name ?? item.id,
        }));
    }
    return [];
};

// --- 7. Export (client-side, giống admin heatmap: build từ data hiện có) ---
export interface LiveEventsMapExportData {
    stats: LiveEventsMapStats | null;
    mapLocations: LocationData[];
    activityLevels: LiveEventAreaItem[];
    eventPerformanceRows: EventPerformanceRow[];
}

/** Export Live Event data ra CSV (admin heatmap dùng API blob; merchant chưa có API nên build client-side). */
export const exportLiveEventsMapCSV = (data: LiveEventsMapExportData): Blob => {
    let csv = 'Live Events Map Report\n';
    csv += `Generated: ${new Date().toLocaleString()}\n\n`;

    if (data.stats) {
        csv += 'Summary\n';
        csv += `Total Events,${data.stats.totalEvents}\n`;
        csv += `Total Events Subtext,${data.stats.totalEventsSubtext}\n`;
        csv += `Total Attendees,${data.stats.totalAttendees}\n`;
        csv += `Total Attendees Subtext,${data.stats.totalAttendeesSubtext}\n`;
        csv += `Avg. Show Rate (%),${data.stats.avgShowRatePercent}\n`;
        csv += `Event-to-Redemption (%),${data.stats.eventToRedemptionPercent}\n\n`;
    }

    csv += 'Map Locations\n';
    csv += 'Location Name,Latitude,Longitude,Redemptions (Attendees),% of Total,Redemption Rate\n';
    (data.mapLocations || []).forEach((loc) => {
        csv += `"${loc.name}",${loc.latitude},${loc.longitude},${loc.redemptions},${loc.percentage_of_total.toFixed(1)},${loc.redemption_rate.toFixed(1)}%\n`;
    });
    csv += '\n';

    csv += 'Activity Levels\n';
    csv += 'Location,Attendees,% of Total,Most Popular Event,Demographics\n';
    (data.activityLevels || []).forEach((a) => {
        csv += `"${a.name}",${a.attendees},${a.percentage_of_total.toFixed(1)}%,"${a.most_popular_event}","${a.demographics}"\n`;
    });
    csv += '\n';

    csv += 'Event Performance Table\n';
    csv += 'Event Name,Date,RSVP,Attended,Show Rate (%)\n';
    (data.eventPerformanceRows || []).forEach((r) => {
        csv += `"${r.eventName}",${r.date},${r.rsvp},${r.attended},${r.showRatePercent.toFixed(1)}%\n`;
    });

    return new Blob([csv], { type: 'text/csv;charset=utf-8;' });
};

/** Export XLSX: cùng nội dung CSV (merchant chưa có API export như admin, giống coupon heatmap trả CSV). */
export const exportLiveEventsMapXLSX = (data: LiveEventsMapExportData): Blob => {
    return exportLiveEventsMapCSV(data);
};
